import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const STATUS = {
  paid: { label: "Pago", cls: "text-emerald-500 border-emerald-500/40" },
  pending: { label: "Pendente", cls: "text-amber-500 border-amber-500/40" },
  informative: { label: "Não sacável", cls: "text-amber-600 dark:text-amber-400 border-amber-500/40" },
};

const Totals = ({ data }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
    <Card className="card-hover p-5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Comissões no período</div>
      <div className="mt-1 font-mono text-2xl font-bold gold-text" data-testid="report-total-paid">{brl(data.total_paid)}</div>
    </Card>
    <Card className="card-hover p-5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Informativo (não sacável)</div>
      <div className="mt-1 font-mono text-2xl font-bold" data-testid="report-total-informative">{brl(data.total_informative)}</div>
    </Card>
    <Card className="card-hover p-5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Entradas</div>
      <div className="mt-1 font-mono text-2xl font-bold" data-testid="report-count">{data.count}</div>
    </Card>
  </div>
);

const ReportTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm min-w-[640px]">
      <thead className="text-xs uppercase tracking-wider text-muted-foreground">
        <tr className="border-b border-border">
          <th className="text-left py-2">Nível</th>
          <th className="text-left">Usuário</th>
          <th className="text-left">Comissão</th>
          <th className="text-left">Data da entrada</th>
          <th className="text-left">Indicado por</th>
        </tr>
      </thead>
      <tbody data-testid="report-table">
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-border/50" data-testid={`report-row-${r.id}`}>
            <td className="py-2 font-mono">{r.level}º</td>
            <td>{r.user_name}</td>
            <td className="font-mono">
              <span className={r.status === "informative" ? "" : "gold-text"}>{brl(r.amount)}</span>{" "}
              <Badge variant="outline" className={`ml-1 text-[10px] ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</Badge>
            </td>
            <td className="text-muted-foreground">{new Date(r.date).toLocaleDateString("pt-BR")}</td>
            <td className="text-muted-foreground">{r.sponsor_name}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function EarningsReport() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback((s, e) => {
    const params = {};
    if (s) params.start = s;
    if (e) params.end = e;
    api.get("/reports/earnings", { params })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => { load("", ""); }, [load]);

  const clear = () => { setStart(""); setEnd(""); load("", ""); };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Relatório</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Relatório de <span className="gold-text">ganhos</span></h1>
        <p className="text-sm text-muted-foreground mt-2">Entradas de cadastros pagantes na sua matriz que geraram comissão.</p>

        <Card className="p-5 mt-6 flex flex-col sm:flex-row sm:items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Data inicial
            <Input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Data final
            <Input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" />
          </label>
          <Button className="gold-btn hover:opacity-95" onClick={() => load(start, end)} data-testid="report-filter-btn">Filtrar</Button>
          <Button variant="outline" onClick={clear} data-testid="report-clear-btn">Limpar</Button>
        </Card>

        {data && <Totals data={data} />}

        <Card className="p-6 mt-6">
          {!data ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : data.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="report-empty">Nenhuma entrada com comissão no período selecionado.</p>
          ) : (
            <ReportTable rows={data.rows} />
          )}
        </Card>
      </div>
    </div>
  );
}
